import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import client from '../api/client';
import EventMessagesTable from './EventMessagesTable';
import './marketingShared.css';

const num = (v) => (v || 0).toLocaleString();
const pct = (v) => (v != null ? `${v}%` : '—');

const EMAIL_COLUMNS = [
  { id: 'created_at', label: 'Date', align: 'left', format: (r) => (r.created_at ? new Date(r.created_at).toLocaleDateString() : '—') },
  { id: 'name', label: 'Name', align: 'left', format: (r) => r.name || '—' },
  { id: 'source', label: 'Source', align: 'left', format: (r) => r.source },
  { id: 'subject', label: 'Subject', align: 'left', format: (r) => r.subject || '—' },
  { id: 'sent', label: 'Sent', align: 'right', format: (r) => num(r.sent), totalFormat: (t) => num(t.sent) },
  { id: 'delivered', label: 'Delivered', align: 'right', format: (r) => num(r.delivered), totalFormat: (t) => num(t.delivered) },
  { id: 'opened', label: 'Opened', align: 'right', format: (r) => num(r.opened), totalFormat: (t) => num(t.opened) },
  { id: 'open_rate', label: 'Open Rate', align: 'right', format: (r) => pct(r.open_rate), totalFormat: (t) => pct(t.open_rate) },
  { id: 'clicked', label: 'Clicked', align: 'right', format: (r) => num(r.clicked), totalFormat: (t) => num(t.clicked) },
  { id: 'click_rate', label: 'Click Rate', align: 'right', format: (r) => pct(r.click_rate), totalFormat: (t) => pct(t.click_rate) },
  { id: 'conversions', label: 'Conversions', align: 'right', format: (r) => num(r.conversions), totalFormat: (t) => num(t.conversions) },
];

const SMS_COLUMNS = EMAIL_COLUMNS.filter((c) => c.id !== 'opened' && c.id !== 'open_rate');

const EmailIcon = (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <rect x="3" y="5" width="18" height="14" rx="2" />
    <path d="m3 7 9 6 9-6" />
  </svg>
);

const SmsIcon = (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M21 11.5a8.38 8.38 0 0 1-8.5 8.5 9 9 0 0 1-3.8-.9L3 20l1.4-4.2A8.5 8.5 0 1 1 21 11.5Z" />
  </svg>
);

const Card = ({ id, title, payload, columns, emptyTitle, emptyLabel, emptyIcon, ctaLabel, ctaHref }) => (
  <section className="mi-messages-card" id={id} aria-label={title}>
    <div className="mi-messages-card-head">
      <h3 className="mi-messages-card-title">{title}</h3>
    </div>
    <EventMessagesTable
      columns={columns}
      rows={payload?.rows || []}
      totals={payload?.totals || {}}
      emptyTitle={emptyTitle}
      emptyLabel={emptyLabel}
      emptyIcon={emptyIcon}
      ctaLabel={ctaLabel}
      ctaHref={ctaHref}
    />
  </section>
);

const EventMessages = ({ teamId, confirmId, data: dataProp }) => {
  const hasInjectedData = dataProp !== undefined;
  const shouldFetch = !hasInjectedData && Boolean(teamId) && Boolean(confirmId);

  const [data, setData] = useState(dataProp || null);
  const [loading, setLoading] = useState(shouldFetch);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!shouldFetch) return undefined;
    const controller = new AbortController();
    setLoading(true);
    setError(null);
    client
      .get(`/teams/${teamId}/confirms/${confirmId}/marketing_insights/event_messages`, { signal: controller.signal })
      .then((response) => setData(response.data))
      .catch((err) => {
        if (err.name === 'CanceledError' || err.name === 'AbortError') return;
        setError(err);
      })
      .finally(() => setLoading(false));
    return () => controller.abort();
  }, [shouldFetch, teamId, confirmId]);

  if (loading) return <div className="mi-audience-loading" role="status">Loading marketing messages…</div>;
  if (error) return <div className="mi-audience-error" role="alert">Couldn't load marketing messages. Refresh to try again.</div>;

  return (
    <>
      <Card
        id="email-marketing"
        title="Email Marketing"
        payload={data?.email}
        columns={EMAIL_COLUMNS}
        emptyIcon={EmailIcon}
        emptyTitle="No email yet"
        emptyLabel="No email has been sent about this event yet. Broadcasts and campaigns tied to this event will show up here."
        ctaLabel="Create Email"
        ctaHref={data?.create_email_path}
      />
      <Card
        id="sms-marketing"
        title="SMS Marketing"
        payload={data?.sms}
        columns={SMS_COLUMNS}
        emptyIcon={SmsIcon}
        emptyTitle="No SMS yet"
        emptyLabel="No SMS messages have been sent about this event yet. Broadcasts and campaigns tied to this event will show up here."
        ctaLabel="Create SMS"
        ctaHref={data?.create_sms_path}
      />
    </>
  );
};

EventMessages.propTypes = {
  teamId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  confirmId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  data: PropTypes.object,
};

export default EventMessages;
